/** The plinth: moulded body, recessed platter tray, rubber feet, contact shadow. */

import * as THREE from 'three';
import { DIM } from '../../geometry/dims.ts';
import { radialShadow } from '../../textures/decals.ts';
import type { DeckMaterials } from '../materials.ts';
import { cyl, roundedBoxGeometry } from '../shapes.ts';

export function buildPlinth(root: THREE.Group, M: DeckMaterials): void {
  const body = new THREE.Mesh(
    roundedBoxGeometry(DIM.deckW, DIM.deckD, DIM.bodyH, 3.0, 0.5, 14),
    M.shellWhite,
  );
  body.position.y = DIM.footH;
  body.castShadow = true;
  body.receiveShadow = true;
  root.add(body);

  /* A thin recessed tray under the platter gives the deck some depth. */
  const recess = new THREE.Mesh(
    cyl(DIM.platter.r + 0.55, DIM.platter.r + 0.55, 0.5, 96),
    M.shellWhiteSoft,
  );
  recess.position.set(DIM.platter.x, DIM.top - 0.24, DIM.platter.z);
  recess.receiveShadow = true;
  root.add(recess);

  const footGeo = cyl(1.5, 1.7, DIM.footH, 24);
  for (const [fx, fz] of [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ] as const) {
    const foot = new THREE.Mesh(footGeo, M.rubber);
    foot.position.set(fx * (DIM.deckW / 2 - 3.4), DIM.footH / 2, fz * (DIM.deckD / 2 - 3.4));
    foot.castShadow = true;
    root.add(foot);
  }

  /* A soft contact shadow, so the deck never looks like it floats. */
  const contact = new THREE.Mesh(
    new THREE.PlaneGeometry(DIM.deckW * 1.5, DIM.deckD * 1.6),
    new THREE.MeshBasicMaterial({
      map: radialShadow(),
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    }),
  );
  contact.rotation.x = -Math.PI / 2;
  contact.position.y = 0.02;
  root.add(contact);
}
